import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { ROUTES } from '@/app/router/routes';
import { useSignup } from './hooks/useSignup';
import { useOnboardingAnalytics } from './hooks/useOnboardingAnalytics';
import { AuthLayout } from './components/AuthLayout';
import { CreateAccountForm } from './components/CreateAccountForm';
import { ProgressHud } from './components/AuthControls';

/**
 * SignUpPage — /signUp.
 *
 * Mirrors Flutter WebSignUpPage (presentation/web/auth/web_signup/web_signup_page.dart): the
 * onboarding panel with the sign-up image and the sign-up form column (WebSignUpFormNew), the
 * footer, and a progress overlay while signing up. While the verify-email card shows, the panel
 * goes back to the onboarding image and its logo logs out (WebOnboardingPage.onLogout).
 * The row behind the columns is primaryColor when onBoardingPage.footerText is set.
 *
 * Flutter source files:
 *   - presentation/web/auth/web_signup/web_signup_page.dart
 *   - application/web_auth/web_signup/web_signup_bloc.dart
 */
export function SignUpPage() {
  const { isAuthorized } = useAuthStore();
  const { content } = useTenantStore();
  const signup = useSignup();
  useOnboardingAnalytics();

  const isVerifyingEmail = signup.phase === 'verifyEmail';

  // Already signed in — but not while the email verification finishes, where useSignup
  // navigates after the verified dialog
  if (isAuthorized && !isVerifyingEmail) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <AuthLayout
      panelImage={isVerifyingEmail ? 'onBoarding' : 'signUp'}
      onLogoClick={isVerifyingEmail ? signup.handleLogout : undefined}
      fillRow={!!content?.contentSettings?.onBoardingPage?.footerText}
    >
      {/* keyed by phase so "Re-sign up" starts from an empty form */}
      <CreateAccountForm key={signup.phase} signup={signup} />
      {signup.isLoading && <ProgressHud />}
    </AuthLayout>
  );
}
